import React, {Fragment, useContext, useEffect, useState} from "react";
import MenuForm, {MenuFormItem} from "../../components/forms/menuForm";
import {Group} from "./dataForGroup";
import {RoutePath} from "../../router/routePath";
import {useHistory} from "react-router-dom";
import {deletePatientGroup, getGroupList, getLastDOSIdByDateTime} from "./groupService";
import Spinner from "../../components/spinner/spinner";
import {ProviderContext} from "../providerContext";
import {DropdownListFormNotSelectedStringId} from "../../components/forms/dropdownListForm";
import {dataForPatientPanel, dataForPreferredLanguage} from "./dataForNewGroup";
import {AgeType, NewGroupSetting} from "./newGroup";
import {PromptContext} from "../promptContext";
import PageButton from "../../components/pageButton/pageButton";
import {mPrint} from "../../util/mPrint";
import CustomModal from "../../components/modal/customModal";
import PatientPreviewList from "./patientPreviewList";
import {ActionType} from "../../util/enum";
import {GrayLog} from "../../grayLog/grayLog";
import {LogEventName} from "../../grayLog/logKey";
import {GraylogContext} from "../graylogContext";

const groupPageSize = 10

function GroupList() {
    const history = useHistory()
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [isNoActiveRecord, setIsNoActiveRecord] = useState<boolean>(false)
    const [groupList, setGroupList] = useState<Array<Group>>([])
    const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false)
    const [showPatientPreviewModal, setShowPatientPreviewModal] = useState<boolean>(false)
    const [editingGroupId, setEditingGroupId] = useState<string>("")
    const provider = useContext(ProviderContext)
    const {showError, showAlert} = useContext(PromptContext)
    const logEmployee = useContext(GraylogContext)

    useEffect(() => {
        refresh()
    }, [page])

    const refresh = () => {
        setIsShowSpinner(true)
        getGroupList(provider.mEmployeeID, page, groupPageSize, (groupList, total) => {
            setIsShowSpinner(false)
            setGroupList(groupList)
            setTotal(total)
            page === 1 && setIsNoActiveRecord(!groupList.length)
        }, (error) => {
            setIsShowSpinner(false)
            showError(error)
        })
    }

    const onDeletePatientGroup = (groupId: string) => {
        deletePatientGroup(groupId, () => {
            const data = groupList.filter((_item) => {
                return _item.id !== groupId
            })
            setGroupList(data)
            GrayLog.event(LogEventName.deleteGroup, {isSuccessful: true, ...logEmployee})
        }, (error) => {
            showError(error)
            const errMsg = `${error.code} - ${error.msg}`
            GrayLog.event(LogEventName.deleteGroup, {isSuccessful: false, errMsg: errMsg, ...logEmployee})
        })
    }

    const onEditPatientGroup = (groupId: string) => {
        const group = groupList.find((_item) => {
            return _item.id === groupId
        })

        if (!group) {
            mPrint("no found target group")
            return
        }

        const {patientFilter, groupName, patientPanel, targetPatientsCount} = group.source
        const {
            patientName,
            fromAge, toAge,
            fromMonth, toMonth,
            gender,
            preferredContact,
            preferredLanguage,
            appointmentType,
            chronicCondition,
            planType,
            insurance,
            icdCodes,
            icdExclusion,
            cptCodes,
            cptExclusion,
            icdPeriodFrom,
            icdPeriodTo,
            cptPeriodFrom,
            cptPeriodTo,
            lastDosFrom,
            lastDosTo,
        } = patientFilter

        const disabledDateTime = "0001-01-01T00:00:00Z"
        const lastDOSId = getLastDOSIdByDateTime(lastDosFrom, lastDosTo)

        let ageType = AgeType.byYear
        if (fromMonth || toMonth) {
            ageType = AgeType.byMonth
        }

        const targetSetting: NewGroupSetting = {
            id: groupId,
            patientName: patientName,
            fromAge: ageType === AgeType.byYear ? (fromAge || "") : (fromMonth || ""),
            toAge: ageType === AgeType.byYear ? (toAge <= 130 ? toAge : "") : (toMonth || ""),
            ageType: ageType,
            gender: gender ? gender : DropdownListFormNotSelectedStringId,
            preferredContact: preferredContact ? preferredContact : DropdownListFormNotSelectedStringId,
            preferredLanguage: preferredLanguage || dataForPreferredLanguage[0].id,
            lastDOS: lastDOSId ? lastDOSId : DropdownListFormNotSelectedStringId,
            dosFrom: lastDosFrom && lastDosFrom !== disabledDateTime ? new Date(lastDosFrom) : null,
            dosTo: lastDosTo && lastDosTo !== disabledDateTime ? new Date(lastDosTo) : null,
            appointmentType: appointmentType,
            chronicCondition: chronicCondition,
            planType: planType && planType.length ? planType[0] : DropdownListFormNotSelectedStringId,
            insurance: insurance && insurance.length ? insurance[0] : DropdownListFormNotSelectedStringId,
            insuranceName: "",
            icd: icdCodes && icdCodes.length ? icdCodes : [],
            excludeIcd: icdExclusion && icdExclusion.length ? icdExclusion : [],
            cpt: cptCodes && cptCodes.length ? cptCodes : [],
            excludeCpt: cptExclusion && cptExclusion.length ? cptExclusion : [],
            icdFrom: icdPeriodFrom && icdPeriodFrom !== disabledDateTime ? new Date(icdPeriodFrom) : null,
            icdTo: icdPeriodTo && icdPeriodTo !== disabledDateTime ? new Date(icdPeriodTo) : null,
            cptFrom: cptPeriodFrom && cptPeriodFrom !== disabledDateTime ? new Date(cptPeriodFrom) : null,
            cptTo: cptPeriodTo && cptPeriodTo !== disabledDateTime ? new Date(cptPeriodTo) : null,
            patientPanel: patientPanel && patientPanel.length ? patientPanel : dataForPatientPanel[0].id,
            groupName: groupName,
        }
        history.push({
            pathname: RoutePath.newGroup,
            search: `?actionType=${ActionType.edit}`,
            state: {
                setting: targetSetting,
                matchingCount: targetPatientsCount,
            },
        })
    }

    const $patientPreviewModal = (
        <CustomModal isOpen={showPatientPreviewModal}>
            <PatientPreviewList
                groupId={editingGroupId}
                actionType={ActionType.edit}
                onClose={() => {
                    setShowPatientPreviewModal(false)
                    refresh()
                }}
            />
        </CustomModal>
    )

    const $dataForMenuForm: Array<MenuFormItem> = [{name: "View Details", id: 0}, {name: "Edit", id: 1}, {name: "Delete", id: 2}]
    const $menuRow = (groupId: string) => {
        return (
            <MenuForm data={$dataForMenuForm} onClick={(id) => {
                setEditingGroupId(groupId)
                if (id === 0) {
                    setShowPatientPreviewModal(true)
                } else if (id === 1) {
                    onEditPatientGroup(groupId)
                } else if (id === 2) {
                    showAlert(
                        "Delete Group",
                        "Are you sure you want to delete the group? This action cannot be undone.",
                        {title: "Confirm", onClick: () => {
                            onDeletePatientGroup(groupId)
                        }},
                        {title: "Cancel", onClick: undefined})
                }
            }}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 14.5229C16.8954 14.5229 16 13.6275 16 12.5229C16 11.4184 16.8954 10.5229 18 10.5229C19.1046 10.5229 20 11.4184 20 12.5229C20 13.0534 19.7893 13.5621 19.4142 13.9372C19.0391 14.3122 18.5304 14.5229 18 14.5229ZM12 14.5229C10.8954 14.5229 10 13.6275 10 12.5229C10 11.4184 10.8954 10.5229 12 10.5229C13.1046 10.5229 14 11.4184 14 12.5229C14 13.0534 13.7893 13.5621 13.4142 13.9372C13.0391 14.3122 12.5304 14.5229 12 14.5229ZM6 14.5229C4.89543 14.5229 4 13.6275 4 12.5229C4 11.4184 4.89543 10.5229 6 10.5229C7.10457 10.5229 8 11.4184 8 12.5229C8 13.0534 7.78929 13.5621 7.41421 13.9372C7.03914 14.3122 6.53043 14.5229 6 14.5229Z" fill="#2F80ED"/>
                </svg>
            </MenuForm>
        )
    }

    const startPageNumber = ((page - 1) * groupPageSize + 1)
    const endPageNumber = (page) * groupPageSize >= total ? total : (page) * groupPageSize
    const pageRange = `${startPageNumber}-${endPageNumber}`
    const $pageIndex = total > 0 ? (
        <div className={"flex flex-row items-center text-sm text-gray-1 space-x-2"}>
            <span className={"text-black font-bold"}>{pageRange}</span>
            <span className={"text-gray-2"}>of</span>
            <span className={"text-gray-3"}>{total}</span>
        </div>) : null

    const onNextPage = () => {
        if (page * groupPageSize > total) {
            return
        }
        setPage(page + 1)
    }
    const onPreviousPage = () => {
        if (page > 1) {
            setPage(page - 1)
        }
    }

    const hasPreviousPage = (page > 1)
    const hasNextPage = !(page * groupPageSize > total)
    const $pageButton = (
        <div className={"flex flex-row items-center justify-center space-x-4"}>
            <PageButton enable={hasPreviousPage} rotate={"rotate-180"} onClick={onPreviousPage} />
            <PageButton enable={hasNextPage} onClick={onNextPage} />
        </div>
    )

    const $tableFooter = (
        <div className={"flex flex-row items-center justify-end w-full pt-4 space-x-20"}>
            {$pageIndex}
            {$pageButton}
        </div>
    )

    const $row = (value: string | number) => {
        return <p className={"text-gray-1 text-sm font-normal"}>{value}</p>
    }
    const $activeCampaignContent = () => {
        return groupList.map((group, idx) => {
            return (
                <tr className={"border-b border-border-black-12"} key={idx}>
                    <td className={"py-3"}>{$row(group.name)}</td>
                    <td className={"py-3 text-center"}>{$row(group.size)}</td>
                    <td className={"py-3"}>{$row(group.lastUpdated)}</td>
                    <td className={"py-3"}>{$row(group.createBy)}</td>
                    <td className={"py-3"}>{$row(group.patientPanel)}</td>
                    <td className={"py-3"}>{$menuRow(group.id)}</td>
                </tr>
            )
        })
    }

    const $tableHeader = (
        <tr className={"text-left text-gray-2 font-bold border-b border-base-border"}>
            <th className={"pb-4 text-sm"}>Group Name</th>
            <th className={"pb-4 text-sm px-6 text-center"}>Size</th>
            <th className={"pb-4 text-sm"}>Last Updated</th>
            <th className={"pb-4 text-sm"}>Created By</th>
            <th className={"pb-4 text-sm"}>Patient Panel</th>
            <th className={"pb-4 text-sm"} />
        </tr>
    )

    const $table = (
        <table className={"w-full"}>
            <thead>
                {$tableHeader}
            </thead>
            <tbody>
                {$activeCampaignContent()}
            </tbody>
        </table>
    )

    const $spinner = <Spinner isOpen={isShowSpinner} />

    const $list = groupList.length ? (
        <div className={"w-full min-w-min mt-4 p-6 flex flex-col items-center bg-white rounded"}>
            {$table}
            {$tableFooter}
        </div>
    ) : null

    const $emptyGroupList = (
        <div className={"flex flex-col items-center mt-4 p-5 space-y-4 rounded shadow-md bg-white"}>
            <p className={"text-3xl font-bold text-gray-1"}>Welcome</p>
            <p className={"text-sm text-gray-1"}>Get started by creating your first Patient Group. You will be able to save your custom groups for future uses.</p>
        </div>
    )

    return (
        <Fragment>
            {isNoActiveRecord ? $emptyGroupList : $list}
            {$patientPreviewModal}
            {$spinner}
        </Fragment>
    )
}

export default GroupList
